import {
  convertCoordinatesToAddress,
  getSpreadsheetInstance,
} from "@/jspreadsheet/jspreadsheet";
import { useMainStore } from "@/stores/main";
import { useSpreadsheetStore } from "@/stores/spreadsheet";
import { SpreadsheetInstance, WorksheetInstance } from "jspreadsheet-ce";
import { debounce } from "lodash";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export const useSpreadsheetHandler = () => {
  const { setActiveSheet, setSelectedCell } = useSpreadsheetStore();
  const setMainSelection = useMainStore((state) => state.selection.set);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleLoad = (instance: SpreadsheetInstance) => {
    if (instance) {
      const tableIdFromUrl = searchParams.get("table");

      let worksheetIdx = instance.getWorksheetActive();

      if (tableIdFromUrl) {
        const targetIdx = instance.worksheets.findIndex(
          (ws) => ws.options.meta?.info?.tableId === tableIdFromUrl,
        );
        if (targetIdx >= 0 && targetIdx !== worksheetIdx) {
          worksheetIdx = targetIdx;
          instance.worksheets[0]?.openWorksheet(targetIdx);
        }
      }

      const worksheet = instance.worksheets[worksheetIdx];
      setActiveSheet(worksheet);
      worksheet.updateSelectionFromCoords(0, 0);

      // SearchParam은 set은 1) worksheet initial load 시 2) tab 변경 시
      if (!tableIdFromUrl && worksheet?.options.meta?.info?.tableId) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("table", worksheet.options.meta?.info?.tableId);
            return newParams;
          },
          { replace: true },
        );
      }
    }
  };

  const handleSelectionChange = debounce(
    (
      instance: WorksheetInstance,
      x1: number,
      y1: number,
      x2: number,
      y2: number,
    ) => {
      const worksheet = instance;
      const startAddress = convertCoordinatesToAddress(x1, y1);
      const endAddress = convertCoordinatesToAddress(x2, y2);

      if (x1 === x2 && y1 === y2) {
        const value = instance.getValueFromCoords(x1, y1);
        setSelectedCell({ address: startAddress, value });
        setMainSelection(
          `${worksheet?.options.worksheetName}!${startAddress}`,
          worksheet?.options.worksheetId || "",
          worksheet?.options.meta?.info?.tableId || "",
        );
      } else {
        // onselection 의 (x1,y1) 은 항상 좌상단으로 정규화되어 드래그 방향(앵커)을
        // 잃는다. 드래그 시작 셀은 selectedCell[0],[1]에 정규화 없이 보존되므로,
        // 이를 앵커로 넘겨 역방향 드래그에서도 fact 가 시작 셀에 고정되게 한다.
        const selected = instance.selectedCell;
        const anchorCol = Number(selected?.[0]);
        const anchorRow = Number(selected?.[1]);
        const anchorAddress =
          Number.isFinite(anchorCol) && Number.isFinite(anchorRow)
            ? convertCoordinatesToAddress(anchorCol, anchorRow)
            : startAddress;
        setMainSelection(
          `${worksheet?.options.worksheetName}!${startAddress}:${endAddress}`,
          worksheet?.options.worksheetId || "",
          worksheet?.options.meta?.info?.tableId || "",
          anchorAddress,
        );
      }
    },
    50,
  );

  return {
    handleLoad,
    handleSelectionChange,
  };
};

/**
 * @description
 * jspreadsheet-ce 는 built-in tab change event 를 지원하지 않음.
 * aria-selected 속성이 변경될 때마다 선택된 시트를 업데이트함.
 */
const useSheetChangeEvent = () => {
  const instance = getSpreadsheetInstance();
  const { setActiveSheet } = useSpreadsheetStore();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!instance) {
      console.warn("Could not find jspreadsheet instance");
      return;
    }

    const container = instance.el || instance.element;
    if (!container) {
      console.warn("Could not find jspreadsheet container");
      return;
    }

    const tabsContainer = container.querySelector(".jtabs-headers");
    if (!tabsContainer) {
      console.warn("Could not find jspreadsheet tabs container");
      return;
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "aria-selected"
        ) {
          const target = mutation.target as HTMLElement;

          if (target.getAttribute("aria-selected") === "true") {
            const tabElements = Array.from(tabsContainer.children).filter(
              (child) => child !== tabsContainer.querySelector(".jtabs-border"),
            );
            const tabIndex = tabElements.indexOf(target);

            if (tabIndex >= 0) {
              const activeWorksheet = instance.worksheets[tabIndex];
              if (activeWorksheet) {
                setActiveSheet(activeWorksheet);
                activeWorksheet.updateSelectionFromCoords(0, 0);

                const tableId = activeWorksheet.options.meta?.info?.tableId;
                if (tableId) {
                  // SearchParam은 set은  1) worksheet initial load 시 2) tab 변경 시
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set("table", tableId);
                      return newParams;
                    },
                    { replace: true },
                  );
                }
              }
            }
          }
        }
      });
    });

    observer.observe(tabsContainer, {
      attributes: true,
      attributeFilter: ["aria-selected"],
      subtree: true,
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
    };
  }, [instance?.element.querySelector(".jtabs-headers"), setSearchParams]);
};

/**
 * @description
 * URL 의 ?table= 파라미터를 활성 워크시트 탭과 동기화한다.
 *
 * handleLoad 는 onload(최초 1회)에만 table 파라미터를 읽어 탭을 전환하므로, 같은
 * 카테고리 내 다른 표로 navigate 하면(=Spreadsheet 리마운트 없이 쿼리만 변경)
 * 탭이 바뀌지 않는다. 이 훅이 파라미터 변화를 구독해 해당 워크시트로 전환한다.
 * (다른 카테고리로 이동하면 key 가 바뀌어 리마운트→handleLoad 가 처리한다.)
 */
const useSyncActiveWorksheetWithUrl = () => {
  const instance = getSpreadsheetInstance();
  const [searchParams] = useSearchParams();
  const tableIdFromUrl = searchParams.get("table");

  useEffect(() => {
    if (!instance || !tableIdFromUrl) return;
    const targetIdx = instance.worksheets.findIndex(
      (ws) => ws.options.meta?.info?.tableId === tableIdFromUrl,
    );
    if (targetIdx < 0 || targetIdx === instance.getWorksheetActive()) return;
    instance.worksheets[0]?.openWorksheet(targetIdx);
  }, [instance, tableIdFromUrl]);
};

export const useSpreadsheetEvent = () => {
  useSheetChangeEvent();
  useSyncActiveWorksheetWithUrl();
};

export * from "./sheet-update";
